<template>
  <section class="flight-document-preview">
    <header class="flight-document-preview__header">
      <div>
        <span>Ticket file</span>
        <strong :title="file.file_name">{{ file.file_name || 'Uploaded ticket' }}</strong>
      </div>
      <a v-if="source" :href="source" target="_blank" rel="noopener">Open ↗</a>
    </header>

    <div v-if="previewError" class="flight-document-preview__state">
      <strong>Preview could not be loaded</strong>
      <p>{{ previewError }}</p>
      <app-button design="new" size="small" @click.native="retry">Try again</app-button>
    </div>
    <template v-else-if="kind === 'pdf'">
      <app-spinner v-if="loading" design="new" />
      <iframe
        :key="previewKey"
        :src="source"
        class="flight-document-preview__frame"
        title="Flight ticket PDF preview"
        @load="onLoaded"
        @error="onError"
      />
    </template>
    <template v-else-if="kind === 'image'">
      <app-spinner v-if="loading" design="new" />
      <div class="flight-document-preview__image-wrap">
        <img
          :key="previewKey"
          :src="source"
          :alt="file.file_name || 'Flight ticket'"
          class="flight-document-preview__image"
          @load="onLoaded"
          @error="onError"
        >
      </div>
    </template>
    <div v-else class="flight-document-preview__state">
      <strong>Preview is unavailable</strong>
      <p>The ticket has no supported PDF or image preview.</p>
      <a v-if="source" :href="source" target="_blank" rel="noopener">Open the file in a new tab</a>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FlightTicketDocumentPreview',
  props: {
    file: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      previewError: null,
      previewKey: 0,
      loadTimer: null,
    }
  },
  computed: {
    source() {
      return this.file.file || this.file.url || null
    },
    extension() {
      const value = this.file.file_name || this.source || ''
      const cleanValue = value.split('?')[0]
      return cleanValue.includes('.') ? cleanValue.split('.').pop().toLowerCase() : ''
    },
    kind() {
      if (!this.source) return 'other'
      if (this.extension === 'pdf') return 'pdf'
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'tiff', 'bmp'].includes(this.extension)) {
        return 'image'
      }
      return 'other'
    },
    previewIdentity() {
      return `${this.kind}:${this.source || ''}`
    },
  },
  watch: {
    previewIdentity: {
      immediate: true,
      handler() {
        this.resetPreview()
      },
    },
  },
  beforeDestroy() {
    this.clearLoadTimer()
  },
  methods: {
    resetPreview() {
      this.clearLoadTimer()
      this.previewError = null
      this.loading = this.kind === 'pdf' || this.kind === 'image'
      if (this.loading) {
        const timeout = this.kind === 'pdf' ? 60000 : 15000
        this.loadTimer = window.setTimeout(() => {
          if (this.loading) {
            this.previewError = 'The file did not finish loading. Check the connection and try again.'
            this.loading = false
          }
        }, timeout)
      }
    },
    retry() {
      this.previewKey += 1
      this.resetPreview()
    },
    onLoaded() {
      this.clearLoadTimer()
      this.loading = false
    },
    onError() {
      this.clearLoadTimer()
      this.loading = false
      this.previewError = 'The file is unavailable or the preview link has expired.'
    },
    clearLoadTimer() {
      window.clearTimeout(this.loadTimer)
      this.loadTimer = null
    },
  },
}
</script>

<style lang="sass" scoped>
.flight-document-preview
  display: flex
  min-height: 0
  flex: 1 1 auto
  flex-direction: column
  overflow: hidden
  border: 1px solid $ui-line
  border-radius: 16px
  background: $ui-surface-soft
  box-shadow: 0 8px 26px rgba(28, 45, 74, .055)

.flight-document-preview__header
  display: flex
  align-items: center
  justify-content: space-between
  gap: 15px
  padding: 13px 15px
  border-bottom: 1px solid $ui-line
  background: $ui-surface

  div
    min-width: 0

  span
    display: block
    margin-bottom: 2px
    color: $color-text-second
    font-size: 12px
    font-weight: 600
    text-transform: uppercase

  strong
    display: block
    overflow: hidden
    font-size: 14px
    text-overflow: ellipsis
    white-space: nowrap

  a
    flex-shrink: 0
    color: $color-theme
    font-size: 14px
    font-weight: 600
    text-decoration: none

.flight-document-preview__frame
  display: block
  width: 100%
  min-height: 380px
  height: auto
  flex: 1 1 auto
  border: 0
  background: $ui-surface

.flight-document-preview__image-wrap
  display: flex
  min-height: 380px
  flex: 1 1 auto
  justify-content: center
  padding: 16px
  overflow: auto

.flight-document-preview__image
  max-width: 100%
  height: auto
  object-fit: contain

.flight-document-preview__state
  display: flex
  align-items: center
  justify-content: center
  flex-direction: column
  gap: 10px
  min-height: 300px
  padding: 30px
  color: $color-text-second
  text-align: center

  strong
    color: $color-text-base

  p
    max-width: 360px
    margin: 0
    font-size: 14px
    line-height: 1.5

  a
    color: $color-theme
    font-weight: 600
    text-decoration: none
</style>
